/**
 * Unity-like GameObject / Rigidbody2D / Collider2D layer on top of planck (Box2D 2.4 port).
 *
 * Step order mirrors Unity's FixedUpdate loop:
 *   Start() of new behaviours -> FixedUpdate() of all behaviours (script execution order) ->
 *   physics step -> trigger / collision callbacks (enter / stay / exit).
 */
import * as planck from 'planck';
import type { SpriteRef } from '../../data/gameData.ts';
import { IDENTITY, apply, compose, invert, mul, type Xform } from '../../model/xform.ts';
import { FIXED_DT, FrameCounter, type RGBA, type V2 } from './unity.ts';
import type { Loop } from './tileCollision.ts';

// ---------- physics materials ----------

export interface PhysMat {
  name: string;
  friction: number;
  bounciness: number;
  /** PhysicsMaterialCombine2D: Average 0, Mean 1, Multiply 2, Minimum 3, Maximum 4 */
  frictionCombine: number;
  bounceCombine: number;
}

export const MATERIALS: Record<string, PhysMat> = {
  a13fb2305aceebf4980c13a4b30e760e: { name: 'PlayerGroundedMaterial', friction: 1, bounciness: 0, frictionCombine: 4, bounceCombine: 4 },
  e10a03fda7eb02e489d829d9150f2092: { name: 'PlayerInAirMaterial', friction: 0, bounciness: 0, frictionCombine: 1, bounceCombine: 4 },
  '8d6c82a5469395049a906f57766450be': { name: 'SpikeBall', friction: 0.4, bounciness: 0.1, frictionCombine: 1, bounceCombine: 4 },
  f42d05fa3e384f0478b4bebd63b92782: { name: 'Marble', friction: 0.7, bounciness: 0.1, frictionCombine: 1, bounceCombine: 4 },
  bf348de5212ce5842900bb6a43166a6f: { name: 'LevelIce', friction: 0.04, bounciness: 0, frictionCombine: 1, bounceCombine: 4 },
  '68102763c7aba674eaefa8e538a7dda5': { name: 'DefaultMaterial', friction: 0.4, bounciness: 0, frictionCombine: 1, bounceCombine: 4 },
  d2f0a6a830e8b9945b538823bf53ef81: { name: 'Volleyball', friction: 1, bounciness: 0.7, frictionCombine: 1, bounceCombine: 4 },
};
/** Physics2D default material (used when neither collider nor rigidbody has one). */
export const DEFAULT_MAT: PhysMat = { name: '', friction: 0.4, bounciness: 0, frictionCombine: 1, bounceCombine: 4 };

export function materialFromRef(r: any): PhysMat | null {
  if (!r || !r.guid) return null;
  return MATERIALS[r.guid] ?? null;
}

function combine(a: number, b: number, ma: number, mb: number): number {
  switch (Math.max(ma, mb)) {
    case 0: return (a + b) / 2;
    case 1: return Math.sqrt(a * b);
    case 2: return a * b;
    case 3: return Math.min(a, b);
    default: return Math.max(a, b);
  }
}

// ---------- layer collision matrix (Physics2DSettings) ----------

/** Default (0) and Debris (3) never collide; everything else does. */
function layerMask(layer: number): number {
  if (layer === 0) return 0xffffffff & ~(1 << 3);
  if (layer === 3) return 0xffffffff & ~1;
  return 0xffffffff;
}

// ---------- types ----------

export const enum BodyType {
  Dynamic = 0,
  Kinematic = 1,
  Static = 2,
}

export interface ContactPoint {
  /** Unity convention: points from the other collider towards this body */
  normal: V2;
  point: V2;
  normalImpulse: number;
  separation: number;
  /** other collider */
  collider: SimCollider;
  /** this body's collider */
  otherCollider: SimCollider;
  /** other collider's attached rigidbody */
  rigidbody: SimBody | null;
  enabled: boolean;
}

export interface Collision2D {
  collider: SimCollider;
  otherCollider: SimCollider;
  rigidbody: SimBody | null;
  gameObject: SimGo;
  contacts: ContactPoint[];
  relativeVelocity: V2;
}

export interface SpriteState {
  sprite: SpriteRef | null;
  color: RGBA;
  flipX: boolean;
  flipY: boolean;
  enabled: boolean;
  sortingLayer: string;
  order: number;
  drawMode: number;
  size: [number, number];
  tileMode?: number;
  tileThreshold?: number;
  /** colours excluded from tinting (SpriteExcludeTintPropertyBlockController) */
  excludeTint?: RGBA;
  /** Unity's built-in 1x1 unit "Square" sprite (not part of the exported textures) */
  square?: boolean;
  /** SpriteRenderer.m_MaskInteraction: 0 none, 1 visible inside mask, 2 visible outside mask */
  maskInteraction?: number;
}

export interface TilemapState {
  key: string;
  tiles: number[];
  palette: { tex: string; sprite: string; tile?: string }[];
  color: RGBA;
  sortingLayer: string;
  order: number;
  enabled: boolean;
}

// ---------- behaviour ----------

export abstract class Behaviour {
  /** Unity script execution order */
  static order = 0;
  /** false for scripts that are not ICheckpointable: their fields survive checkpoint restores */
  static checkpointable = true;
  enabled = true;
  started = false;
  readonly go: SimGo;
  readonly data: any;
  constructor(go: SimGo, data: any = {}) {
    this.go = go;
    this.data = data;
  }
  get world(): SimWorld {
    return this.go.world;
  }
  get order(): number {
    return (this.constructor as typeof Behaviour).order;
  }
  awake?(): void;
  start?(): void;
  fixedUpdate?(): void;
  /** per render frame (cosmetic), dt in seconds */
  update?(dt: number): void;
  /** after every update() of the frame (camera, parallax) */
  lateUpdate?(dt: number): void;
  onTriggerEnter?(other: SimCollider, self: SimCollider): void;
  onTriggerStay?(other: SimCollider, self: SimCollider): void;
  onTriggerExit?(other: SimCollider, self: SimCollider): void;
  onCollisionEnter?(c: Collision2D): void;
  onCollisionStay?(c: Collision2D): void;
  onCollisionExit?(c: Collision2D): void;
  onDestroy?(): void;
  /** called after a checkpoint restore (re-derive cached visuals) */
  afterRestore?(): void;
  /** field names that are not part of checkpoint state */
  noSave?: string[];
}

// ---------- rigidbody ----------

export class SimBody {
  readonly go: SimGo;
  readonly pb: planck.Body;
  private _type: BodyType;
  mass: number;
  useAutoMass: boolean;
  material: PhysMat | null = null;
  freezeX = false;
  freezeY = false;
  simulated = true;
  /** KinematicVelocity component registered */
  hasKV = false;
  kvVelocity: V2 = { x: 0, y: 0 };
  private kvOld: V2;
  moveTarget: V2 | null = null;
  moveRotTarget: number | null = null;
  private savedVel: V2 | null = null;
  private savedAng: number | null = null;
  private frozenPos: V2 | null = null;

  constructor(go: SimGo, type: BodyType, opts: { mass?: number; useAutoMass?: boolean; gravityScale?: number; linearDamping?: number; angularDamping?: number; freezeRotation?: boolean; continuous?: boolean; sleep?: boolean; freezeX?: boolean; freezeY?: boolean }) {
    this.go = go;
    this._type = type;
    this.mass = opts.mass ?? 1;
    this.useAutoMass = opts.useAutoMass ?? false;
    const w = go.world;
    const m = go.worldXform();
    this.pb = w.pw.createBody({
      type: planckType(type),
      position: { x: m.tx, y: m.ty },
      angle: Math.atan2(m.b, m.a),
      gravityScale: opts.gravityScale ?? 1,
      linearDamping: opts.linearDamping ?? 0,
      angularDamping: opts.angularDamping ?? 0.05,
      fixedRotation: !!opts.freezeRotation,
      bullet: !!opts.continuous,
      allowSleep: opts.sleep ?? true,
    });
    this.pb.setUserData(this);
    this.freezeX = !!opts.freezeX;
    this.freezeY = !!opts.freezeY;
    this.kvOld = { x: m.tx, y: m.ty };
    this.updateMass();
  }

  get type(): BodyType {
    return this._type;
  }
  set type(t: BodyType) {
    if (t === this._type) return;
    this._type = t;
    this.pb.setType(planckType(t));
    this.updateMass();
    this.pb.setAwake(true);
  }
  get isDynamic() {
    return this._type === BodyType.Dynamic;
  }

  updateMass() {
    if (this._type !== BodyType.Dynamic) return;
    this.pb.resetMassData();
    if (this.useAutoMass) return;
    const md = { mass: 0, center: planck.Vec2.zero(), I: 0 };
    this.pb.getMassData(md);
    const scale = md.mass > 0 ? this.mass / md.mass : 1;
    const c = md.center;
    // getMassData returns I about the body origin; convert to centroid inertia before scaling
    const Ic = md.I - md.mass * (c.x * c.x + c.y * c.y);
    const I = md.mass > 0 ? Ic * scale + this.mass * (c.x * c.x + c.y * c.y) : this.mass * 0.1;
    this.pb.setMassData({ mass: this.mass, center: c, I: Math.max(I, 1e-6) });
  }

  get position(): V2 {
    const p = this.pb.getPosition();
    return { x: p.x, y: p.y };
  }
  set position(p: V2) {
    this.pb.setPosition(new planck.Vec2(p.x, p.y));
    this.pb.setAwake(true);
    this.go.markDirty();
    this.updateFollowLocal();
  }
  get rotation(): number {
    return (this.pb.getAngle() * 180) / Math.PI;
  }
  set rotation(deg: number) {
    this.pb.setAngle((deg * Math.PI) / 180);
    this.pb.setAwake(true);
    this.go.markDirty();
    this.updateFollowLocal();
  }
  /** non-dynamic rigidbodies below another rigidbody move with their parent transform */
  /** moved explicitly by a script (MovingPlatformHandler) instead of following its parent body */
  managed = false;
  followsParent(): boolean {
    return !this.managed && this._type !== BodyType.Dynamic && !!this.go.parent?.attachedBody();
  }
  private updateFollowLocal() {
    if (!this.followsParent()) return;
    this.go.local = mul(invert(this.go.parent!.worldXform()), this.go.worldXform());
  }
  get velocity(): V2 {
    const v = this.pb.getLinearVelocity();
    return { x: v.x, y: v.y };
  }
  set velocity(v: V2) {
    if (this._type === BodyType.Static) return;
    this.pb.setLinearVelocity(new planck.Vec2(v.x, v.y));
    if (v.x || v.y) this.pb.setAwake(true);
  }
  get vx() {
    return this.pb.getLinearVelocity().x;
  }
  set vx(x: number) {
    this.velocity = { x, y: this.vy };
  }
  get vy() {
    return this.pb.getLinearVelocity().y;
  }
  set vy(y: number) {
    this.velocity = { x: this.vx, y };
  }
  get angularVelocity(): number {
    return (this.pb.getAngularVelocity() * 180) / Math.PI;
  }
  set angularVelocity(d: number) {
    if (this._type === BodyType.Static) return;
    this.pb.setAngularVelocity((d * Math.PI) / 180);
  }
  get gravityScale() {
    return this.pb.getGravityScale();
  }
  set gravityScale(g: number) {
    this.pb.setGravityScale(g);
  }
  get linearDamping() {
    return this.pb.getLinearDamping();
  }
  set linearDamping(d: number) {
    this.pb.setLinearDamping(d);
  }
  set freezeRotation(f: boolean) {
    this.pb.setFixedRotation(f);
    this.updateMass();
  }
  addForce(f: V2) {
    if (this._type !== BodyType.Dynamic) return;
    this.pb.applyForceToCenter(new planck.Vec2(f.x, f.y), true);
  }
  addForceAtPosition(f: V2, p: V2) {
    if (this._type !== BodyType.Dynamic) return;
    this.pb.applyForce(new planck.Vec2(f.x, f.y), new planck.Vec2(p.x, p.y), true);
  }
  addImpulse(f: V2) {
    if (this._type !== BodyType.Dynamic) return;
    this.pb.applyLinearImpulse(new planck.Vec2(f.x, f.y), this.pb.getWorldCenter(), true);
  }
  /** AddTorque(t, ForceMode2D.Impulse); Unity hands the value to Box2D unconverted (radian units) */
  addTorqueImpulse(t: number) {
    if (this._type !== BodyType.Dynamic) return;
    this.pb.applyAngularImpulse(t, true);
  }
  movePosition(p: V2) {
    this.moveTarget = { x: p.x, y: p.y };
  }
  moveRotation(deg: number) {
    this.moveRotTarget = deg;
  }
  setSimulated(s: boolean) {
    this.simulated = s;
    this.syncActive();
  }
  syncActive() {
    const want = this.simulated && this.go.active && !this.go.destroyed;
    if (this.pb.isActive() !== want) this.pb.setActive(want);
  }

  /** @internal before the physics step */
  preStep() {
    if (this.freezeX || this.freezeY) this.frozenPos = this.position;
    if (this.moveTarget) {
      const p = this.pb.getPosition();
      this.savedVel = this.velocity;
      this.pb.setLinearVelocity(new planck.Vec2((this.moveTarget.x - p.x) / FIXED_DT, (this.moveTarget.y - p.y) / FIXED_DT));
      this.pb.setAwake(true);
      this.moveTarget = null;
    }
    if (this.moveRotTarget !== null) {
      const a = this.pb.getAngle();
      this.savedAng = this.pb.getAngularVelocity();
      this.pb.setAngularVelocity(((this.moveRotTarget * Math.PI) / 180 - a) / FIXED_DT);
      this.pb.setAwake(true);
      this.moveRotTarget = null;
    }
  }
  /** @internal after the physics step */
  postStep() {
    if (this.savedVel) {
      this.pb.setLinearVelocity(new planck.Vec2(this.savedVel.x, this.savedVel.y));
      this.savedVel = null;
    }
    if (this.savedAng !== null) {
      this.pb.setAngularVelocity(this.savedAng);
      this.savedAng = null;
    }
    if (this.frozenPos) {
      const p = this.pb.getPosition();
      const v = this.pb.getLinearVelocity();
      this.pb.setPosition(new planck.Vec2(this.freezeX ? this.frozenPos.x : p.x, this.freezeY ? this.frozenPos.y : p.y));
      this.pb.setLinearVelocity(new planck.Vec2(this.freezeX ? 0 : v.x, this.freezeY ? 0 : v.y));
      this.frozenPos = null;
    }
    const p = this.pb.getPosition();
    this.kvVelocity = { x: (p.x - this.kvOld.x) / FIXED_DT, y: (p.y - this.kvOld.y) / FIXED_DT };
    this.kvOld = { x: p.x, y: p.y };
  }
  resetKV() {
    const p = this.pb.getPosition();
    this.kvOld = { x: p.x, y: p.y };
    this.kvVelocity = { x: 0, y: 0 };
  }

  getContacts(): ContactPoint[] {
    return this.go.world.contactsOf(this);
  }

  /** world-space AABB of all attached enabled colliders */
  bounds(): [number, number, number, number] | null {
    let b: [number, number, number, number] | null = null;
    for (let f = this.pb.getFixtureList(); f; f = f.getNext()) {
      const fb = fixtureAABB(f);
      b = b ? [Math.min(b[0], fb[0]), Math.min(b[1], fb[1]), Math.max(b[2], fb[2]), Math.max(b[3], fb[3])] : fb;
    }
    return b;
  }
}

function ignoredPair(a: SimCollider, b: SimCollider): boolean {
  return !!(a.ignores?.(b) || b.ignores?.(a));
}

function planckType(t: BodyType): planck.BodyType {
  return t === BodyType.Dynamic ? 'dynamic' : t === BodyType.Kinematic ? 'kinematic' : 'static';
}

function fixtureAABB(f: planck.Fixture): [number, number, number, number] {
  const shape = f.getShape();
  const xf = f.getBody().getTransform();
  let b: [number, number, number, number] | null = null;
  const aabb = new planck.AABB();
  for (let i = 0; i < shape.getChildCount(); i++) {
    shape.computeAABB(aabb, xf, i);
    const lo = aabb.lowerBound, hi = aabb.upperBound;
    b = b ? [Math.min(b[0], lo.x), Math.min(b[1], lo.y), Math.max(b[2], hi.x), Math.max(b[3], hi.y)] : [lo.x, lo.y, hi.x, hi.y];
  }
  return b ?? [0, 0, 0, 0];
}

// ---------- colliders ----------

export type ColliderShape =
  | { kind: 'box'; w: number; h: number; ox: number; oy: number; edge: number }
  | { kind: 'circle'; r: number; ox: number; oy: number }
  | { kind: 'polygon'; paths: Loop[]; ox: number; oy: number }
  | { kind: 'capsule'; w: number; h: number; ox: number; oy: number; dir: number }
  | { kind: 'edge'; points: Loop; ox: number; oy: number; edge: number }
  | { kind: 'chains'; loops: Loop[] };

export interface Effector {
  /** surface arc in degrees */
  arc: number;
  useOneWay: boolean;
}

export class SimCollider {
  readonly go: SimGo;
  shape: ColliderShape;
  isTrigger: boolean;
  private _enabled: boolean;
  material: PhysMat | null;
  usedByEffector = false;
  /** PlatformEffector2D on this collider's GameObject */
  effector: Effector | null = null;
  density: number;
  fixtures: planck.Fixture[] = [];
  /** Unity type name (BoxCollider2D, ...) */
  type: string;
  /** Physics2D.IgnoreCollision for the pairs it returns true for: no response, contacts or callbacks */
  ignores: ((other: SimCollider) => boolean) | null = null;

  constructor(go: SimGo, type: string, shape: ColliderShape, opts: { isTrigger?: boolean; enabled?: boolean; material?: PhysMat | null; density?: number; usedByEffector?: boolean } = {}) {
    this.go = go;
    this.type = type;
    this.shape = shape;
    this.isTrigger = !!opts.isTrigger;
    this._enabled = opts.enabled ?? true;
    this.material = opts.material ?? null;
    this.density = opts.density ?? 1;
    this.usedByEffector = !!opts.usedByEffector;
  }

  get enabled() {
    return this._enabled;
  }
  set enabled(e: boolean) {
    if (this._enabled === e) return;
    this._enabled = e;
    this.sync();
  }

  /** Collider2D.attachedRigidbody */
  get attachedRigidbody(): SimBody | null {
    return this.go.attachedBody();
  }
  get gameObject(): SimGo {
    return this.go;
  }
  /** effective material for contact mixing (collider, else rigidbody, else default) */
  effectiveMaterial(): PhysMat {
    return this.material ?? this.attachedRigidbody?.material ?? DEFAULT_MAT;
  }
  setTrigger(t: boolean) {
    if (this.isTrigger === t) return;
    this.isTrigger = t;
    for (const f of this.fixtures) f.setSensor(t);
  }
  setShape(s: ColliderShape) {
    this.shape = s;
    this.rebuild();
  }
  rebuild() {
    this.destroyFixtures();
    this.sync();
  }

  shouldExist(): boolean {
    return this._enabled && this.go.active && !this.go.destroyed;
  }

  sync() {
    const want = this.shouldExist();
    if (want && !this.fixtures.length) this.createFixtures();
    else if (!want && this.fixtures.length) this.destroyFixtures();
  }

  destroyFixtures() {
    for (const f of this.fixtures) f.getBody().destroyFixture(f);
    const had = this.fixtures.length > 0;
    this.fixtures = [];
    if (had) this.attachedRigidbody?.updateMass();
  }

  private createFixtures() {
    const w = this.go.world;
    const rb = this.attachedRigidbody;
    const pb = rb ? rb.pb : w.ground;
    // transform from collider GO space to the planck body frame (position + rotation, no scale)
    const bodyFrame = rb ? rb.go.bodyFrame() : IDENTITY;
    const rel = mul(invert(bodyFrame), this.go.worldXform());
    const shapes = buildShapes(this.shape, rel);
    const layer = this.go.layer;
    for (const s of shapes) {
      const f = pb.createFixture(s, {
        density: this.density,
        friction: 0,
        restitution: 0,
        isSensor: this.isTrigger,
        filterCategoryBits: 1 << layer,
        filterMaskBits: layerMask(layer),
        userData: this,
      });
      this.fixtures.push(f);
    }
    rb?.updateMass();
  }

  /** world-space AABB */
  bounds(): [number, number, number, number] | null {
    let b: [number, number, number, number] | null = null;
    for (const f of this.fixtures) {
      const fb = fixtureAABB(f);
      b = b ? [Math.min(b[0], fb[0]), Math.min(b[1], fb[1]), Math.max(b[2], fb[2]), Math.max(b[3], fb[3])] : fb;
    }
    return b;
  }
  boundsCenter(): V2 {
    const b = this.bounds();
    if (!b) return this.go.position;
    return { x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2 };
  }

  /** Overlap test against another collider (Collider2D.IsTouching / Distance < 0 analogue). */
  overlaps(other: SimCollider): boolean {
    for (const fa of this.fixtures) for (const fb of other.fixtures) if (fixturesOverlap(fa, fb)) return true;
    return false;
  }
}

const POLY_RADIUS = 0.01; // Physics2D default contact offset

function xfPts(m: Xform, pts: [number, number][]): planck.Vec2[] {
  return pts.map(([x, y]) => {
    const [px, py] = apply(m, x, y);
    return new planck.Vec2(px, py);
  });
}

function isConvexCCW(pts: planck.Vec2[]): boolean {
  const n = pts.length;
  if (n < 3) return false;
  let sgn = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n], c = pts[(i + 2) % n];
    const cr = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cr) < 1e-9) continue;
    const s = Math.sign(cr);
    if (sgn && s !== sgn) return false;
    sgn = s;
  }
  return sgn !== 0;
}

function triangulate(pts: planck.Vec2[]): planck.Vec2[][] {
  // ear clipping (simple polygons)
  let area = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) area += pts[j].x * pts[i].y - pts[i].x * pts[j].y;
  const v = area < 0 ? [...pts].reverse() : [...pts];
  const tris: planck.Vec2[][] = [];
  let guard = 0;
  while (v.length > 3 && guard++ < 10000) {
    let clipped = false;
    for (let i = 0; i < v.length; i++) {
      const a = v[(i + v.length - 1) % v.length], b = v[i], c = v[(i + 1) % v.length];
      const cr = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
      if (cr <= 1e-12) continue;
      let inside = false;
      for (const p of v) {
        if (p === a || p === b || p === c) continue;
        const d1 = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
        const d2 = (c.x - b.x) * (p.y - b.y) - (c.y - b.y) * (p.x - b.x);
        const d3 = (a.x - c.x) * (p.y - c.y) - (a.y - c.y) * (p.x - c.x);
        if (d1 >= 0 && d2 >= 0 && d3 >= 0) { inside = true; break; }
      }
      if (inside) continue;
      tris.push([a, b, c]);
      v.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) break;
  }
  if (v.length === 3) tris.push(v);
  return tris;
}

function polyShape(pts: planck.Vec2[], radius = POLY_RADIUS): planck.Shape[] {
  const out: planck.Shape[] = [];
  const pieces = pts.length <= 8 && isConvexCCW(pts) ? [pts] : triangulate(pts);
  for (const p of pieces) {
    let a = 0;
    for (let i = 0, j = p.length - 1; i < p.length; j = i++) a += p[j].x * p[i].y - p[i].x * p[j].y;
    if (Math.abs(a) < 1e-8) continue;
    const s = new planck.Polygon(p);
    s.m_radius = radius;
    out.push(s);
  }
  return out;
}

function dedupe(pts: planck.Vec2[], closed: boolean): planck.Vec2[] {
  const out: planck.Vec2[] = [];
  const minD = 0.006;
  for (const p of pts) {
    const q = out[out.length - 1];
    if (!q || Math.hypot(p.x - q.x, p.y - q.y) > minD) out.push(p);
  }
  if (closed && out.length > 1) {
    const a = out[0], b = out[out.length - 1];
    if (Math.hypot(a.x - b.x, a.y - b.y) <= minD) out.pop();
  }
  return out;
}

function buildShapes(s: ColliderShape, m: Xform): planck.Shape[] {
  const sx = Math.hypot(m.a, m.b), sy = Math.hypot(m.c, m.d);
  switch (s.kind) {
    case 'box': {
      const hw = Math.abs(s.w) / 2, hh = Math.abs(s.h) / 2;
      if (hw < 1e-4 || hh < 1e-4) return [];
      const pts = xfPts(m, [[s.ox - hw, s.oy - hh], [s.ox + hw, s.oy - hh], [s.ox + hw, s.oy + hh], [s.ox - hw, s.oy + hh]]);
      const det = m.a * m.d - m.b * m.c;
      if (det < 0) pts.reverse();
      return polyShape(pts, s.edge * Math.max(sx, sy) + POLY_RADIUS);
    }
    case 'circle': {
      const [cx, cy] = apply(m, s.ox, s.oy);
      return [new planck.Circle(new planck.Vec2(cx, cy), Math.max(1e-3, s.r * Math.max(sx, sy)))];
    }
    case 'capsule': {
      const vertical = s.dir === 0;
      const r = (vertical ? s.w : s.h) / 2;
      const len = Math.max(0, (vertical ? s.h : s.w) / 2 - r);
      const out: planck.Shape[] = [];
      const c1 = vertical ? [s.ox, s.oy - len] : [s.ox - len, s.oy];
      const c2 = vertical ? [s.ox, s.oy + len] : [s.ox + len, s.oy];
      const rr = r * Math.max(sx, sy);
      for (const c of [c1, c2]) {
        const [x, y] = apply(m, c[0], c[1]);
        out.push(new planck.Circle(new planck.Vec2(x, y), rr));
      }
      if (len > 1e-4) {
        const hw = vertical ? r : len, hh = vertical ? len : r;
        const pts = xfPts(m, [[s.ox - hw, s.oy - hh], [s.ox + hw, s.oy - hh], [s.ox + hw, s.oy + hh], [s.ox - hw, s.oy + hh]]);
        out.push(...polyShape(pts));
      }
      return out;
    }
    case 'polygon': {
      const out: planck.Shape[] = [];
      for (const path of s.paths) {
        let pts = dedupe(xfPts(m, path.map(([x, y]) => [x + s.ox, y + s.oy] as [number, number])), true);
        if (pts.length < 3) continue;
        let a = 0;
        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += pts[j].x * pts[i].y - pts[i].x * pts[j].y;
        if (a < 0) pts = pts.reverse();
        out.push(...polyShape(pts));
      }
      return out;
    }
    case 'edge': {
      const pts = dedupe(xfPts(m, s.points.map(([x, y]) => [x + s.ox, y + s.oy] as [number, number])), false);
      if (pts.length < 2) return [];
      const c = new planck.Chain(pts, false);
      c.m_radius = s.edge + POLY_RADIUS;
      return [c];
    }
    case 'chains': {
      const out: planck.Shape[] = [];
      for (const l of s.loops) {
        const pts = dedupe(xfPts(m, l), true);
        if (pts.length < 3) continue;
        const c = new planck.Chain(pts, true);
        c.m_radius = POLY_RADIUS;
        out.push(c);
      }
      return out;
    }
  }
}

function fixturesOverlap(fa: planck.Fixture, fb: planck.Fixture): boolean {
  const sa = fa.getShape(), sb = fb.getShape();
  const xa = fa.getBody().getTransform(), xb = fb.getBody().getTransform();
  for (let i = 0; i < sa.getChildCount(); i++) for (let j = 0; j < sb.getChildCount(); j++) if (planck.testOverlap(sa, i, sb, j, xa, xb)) return true;
  return false;
}

// ---------- GameObject ----------

let goSerial = 0;

export class SimGo {
  readonly world: SimWorld;
  readonly uid = ++goSerial;
  name: string;
  parent: SimGo | null = null;
  children: SimGo[] = [];
  private _activeSelf = true;
  destroyed = false;
  /** Unity layer index */
  layer = 0;
  tag = '';
  /** excluded from checkpoint snapshots (objects without ICheckpointable components keep their live state) */
  noSnapshot = false;
  /** transform relative to parent (or world when root) */
  local: Xform = IDENTITY;
  body: SimBody | null = null;
  /** lossy scale used to compose a body-driven world transform */
  private bodyScale: [number, number] = [1, 1];
  colliders: SimCollider[] = [];
  behaviours: Behaviour[] = [];
  sprite: SpriteState | null = null;
  tilemap: TilemapState | null = null;
  /** raw Unity components (type -> data) for lookups */
  comps = new Map<string, any>();
  /** stable key for references and debugging */
  key: string;
  /** cosmetic offset applied to this subtree when rendering (Oscillator) */
  renderOffset: V2 | null = null;
  /** extra world-space offsets at which this object's own sprite is drawn again (parallax panel copies) */
  renderCopies: V2[] | null = null;
  private worldCache: Xform | null = null;

  constructor(world: SimWorld, name: string, parent: SimGo | null, local: Xform = IDENTITY, key?: string) {
    this.world = world;
    this.name = name;
    this.local = local;
    this.key = key ?? `go${this.uid}`;
    if (parent) {
      this.parent = parent;
      parent.children.push(this);
      this.layer = parent.layer;
    }
    world.addGo(this);
  }

  get activeSelf() {
    return this._activeSelf;
  }
  get active(): boolean {
    return this._activeSelf && !this.destroyed && (this.parent ? this.parent.active : true);
  }
  setActive(a: boolean) {
    if (this._activeSelf === a) return;
    this._activeSelf = a;
    this.syncPhysics();
  }
  /** re-sync physics existence for this subtree (activity / destruction changes) */
  syncPhysics() {
    const walk = (g: SimGo) => {
      g.body?.syncActive();
      for (const c of g.colliders) c.sync();
      g.children.forEach(walk);
    };
    walk(this);
  }

  getBehaviour<T extends Behaviour>(cls: abstract new (...a: any[]) => T): T | null {
    for (const b of this.behaviours) if (b instanceof cls) return b as T;
    return null;
  }
  getBehaviourInChildren<T extends Behaviour>(cls: abstract new (...a: any[]) => T, includeInactive = false): T | null {
    const own = this.getBehaviour(cls);
    if (own && (includeInactive || this.active)) return own;
    for (const c of this.children) {
      if (!includeInactive && !c.active) continue;
      const r = c.getBehaviourInChildren(cls, includeInactive);
      if (r) return r;
    }
    return null;
  }
  getBehavioursInChildren<T extends Behaviour>(cls: abstract new (...a: any[]) => T, includeInactive = true): T[] {
    const out: T[] = [];
    const walk = (g: SimGo) => {
      if (!includeInactive && !g.active) return;
      for (const b of g.behaviours) if (b instanceof cls) out.push(b as T);
      g.children.forEach(walk);
    };
    walk(this);
    return out;
  }
  getBehaviourInParent<T extends Behaviour>(cls: abstract new (...a: any[]) => T): T | null {
    for (let g: SimGo | null = this; g; g = g.parent) {
      const b = g.getBehaviour(cls);
      if (b) return b;
    }
    return null;
  }
  collidersInChildren(): SimCollider[] {
    const out: SimCollider[] = [];
    const walk = (g: SimGo) => {
      out.push(...g.colliders);
      g.children.forEach(walk);
    };
    walk(this);
    return out;
  }
  descendants(): SimGo[] {
    const out: SimGo[] = [];
    const walk = (g: SimGo) => {
      out.push(g);
      g.children.forEach(walk);
    };
    walk(this);
    return out;
  }
  findChild(name: string): SimGo | null {
    for (const c of this.children) if (c.name === name) return c;
    for (const c of this.children) {
      const r = c.findChild(name);
      if (r) return r;
    }
    return null;
  }

  /** nearest Rigidbody2D on this GameObject or its ancestors */
  attachedBody(): SimBody | null {
    for (let g: SimGo | null = this; g; g = g.parent) if (g.body) return g.body;
    return null;
  }

  markDirty() {
    const walk = (g: SimGo) => {
      g.worldCache = null;
      g.children.forEach(walk);
    };
    walk(this);
  }

  get bodyScaleXY(): [number, number] {
    return this.bodyScale;
  }
  /** change the scale of a rigidbody-driven object (transform.localScale for root bodies) */
  setBodyScale(sx: number, sy: number) {
    if (this.bodyScale[0] === sx && this.bodyScale[1] === sy) return;
    this.bodyScale = [sx, sy];
    this.markDirty();
    for (const g of this.descendants()) for (const c of g.colliders) if (c.fixtures.length) c.rebuild();
  }
  /** set before a body is attached: remember the lossy scale of the initial transform */
  captureBodyScale() {
    const m = this.worldXform();
    const sx = Math.hypot(m.a, m.b);
    const det = m.a * m.d - m.b * m.c;
    this.bodyScale = [sx || 1, det / (sx || 1) || 1];
  }

  worldXform(): Xform {
    if (this.worldCache) return this.worldCache;
    let m: Xform;
    if (this.body) {
      const p = this.body.pb.getPosition();
      m = compose(p.x, p.y, (this.body.pb.getAngle() * 180) / Math.PI, this.bodyScale[0], this.bodyScale[1]);
    } else {
      m = this.parent ? mul(this.parent.worldXform(), this.local) : this.local;
    }
    this.worldCache = m;
    return m;
  }

  /** position + rotation only (planck body frame) */
  bodyFrame(): Xform {
    const m = this.worldXform();
    const ang = Math.atan2(m.b, m.a);
    return compose(m.tx, m.ty, (ang * 180) / Math.PI);
  }

  get position(): V2 {
    const m = this.worldXform();
    return { x: m.tx, y: m.ty };
  }
  set position(p: V2) {
    if (this.body) {
      this.body.position = p;
      return;
    }
    const pw = this.parent ? this.parent.worldXform() : IDENTITY;
    const ip = invert(pw);
    const [lx, ly] = apply(ip, p.x, p.y);
    this.local = { ...this.local, tx: lx, ty: ly };
    this.markDirty();
    this.world.transformMoved(this);
  }
  /** world rotation in degrees */
  get rotation(): number {
    const m = this.worldXform();
    return (Math.atan2(m.b, m.a) * 180) / Math.PI;
  }
  get lossyScale(): [number, number] {
    const m = this.worldXform();
    const sx = Math.hypot(m.a, m.b);
    const det = m.a * m.d - m.b * m.c;
    return [sx, det / (sx || 1)];
  }
  /** transform.up */
  get up(): V2 {
    const m = this.worldXform();
    const l = Math.hypot(m.c, m.d) || 1;
    return { x: m.c / l, y: m.d / l };
  }
  setLocal(m: Xform) {
    this.local = m;
    this.markDirty();
    this.world.transformMoved(this);
  }

  destroy() {
    if (this.destroyed) return;
    for (const g of this.descendants()) for (const b of g.behaviours) b.onDestroy?.();
    this.destroyed = true;
    this.syncPhysics();
    this.world.onGoDestroyed(this);
  }
}

// ---------- world ----------

export interface GoState {
  activeSelf: boolean;
  destroyed: boolean;
  local: Xform;
  scale: [number, number];
  body?: { type: BodyType; pos: V2; angle: number; vel: V2; angVel: number; simulated: boolean; gravityScale: number; mass: number; kv: V2 };
  colliders: { enabled: boolean; isTrigger: boolean; shape: ColliderShape; material: PhysMat | null }[];
  sprite?: SpriteState;
  tilemap?: { tiles: number[]; color: RGBA; enabled: boolean; order: number; sortingLayer: string };
  behaviours: Record<string, any>[];
}

export interface WorldSnapshot {
  frame: number;
  gos: Map<SimGo, GoState>;
  extra: any;
  /** includes noSnapshot objects and non-checkpointable behaviours (scene reload) */
  full: boolean;
}

const isPlain = (v: any) => v !== null && typeof v === 'object' && (Array.isArray(v) || Object.getPrototypeOf(v) === Object.prototype);

function cloneValue(v: any): any {
  if (v === null || typeof v !== 'object') return v;
  if (v instanceof FrameCounter) return v.copy();
  if (typeof v.cloneState === 'function') return v.cloneState();
  if (v instanceof Set) return new Set(v);
  if (v instanceof Map) return new Map(v);
  if (isPlain(v)) {
    if (Array.isArray(v)) return v.map(cloneValue);
    const o: any = {};
    for (const k of Object.keys(v)) o[k] = cloneValue(v[k]);
    return o;
  }
  // class instances (SimGo, Behaviour, SimBody, sprites...) are references
  return v;
}

const BEHAVIOUR_SKIP = new Set(['go', 'data', 'noSave', 'ctx', 'unityType', 'listeners']);

export function saveBehaviour(b: Behaviour, full = false): Record<string, any> {
  const out: Record<string, any> = {};
  if (!full && !(b.constructor as typeof Behaviour).checkpointable) return out;
  const skip = b.noSave;
  for (const k of Object.keys(b)) {
    if (BEHAVIOUR_SKIP.has(k) || (skip && skip.includes(k))) continue;
    const v = (b as any)[k];
    if (typeof v === 'function') continue;
    out[k] = cloneValue(v);
  }
  return out;
}

export function loadBehaviour(b: Behaviour, s: Record<string, any>) {
  for (const k of Object.keys(s)) (b as any)[k] = cloneValue(s[k]);
}

interface PairState {
  a: SimCollider;
  b: SimCollider;
  sensor: boolean;
  seen: number;
}

export class SimWorld {
  readonly pw: planck.World;
  readonly ground: planck.Body;
  gos: SimGo[] = [];
  behaviours: Behaviour[] = [];
  private pendingStart: Behaviour[] = [];
  private behavioursDirty = false;
  frame = 0;
  time = 0;
  private pairs = new Map<string, PairState>();
  /** one-way decisions per planck contact */
  private oneWay = new WeakMap<planck.Contact, boolean>();
  /** snapshots that keep destroyed objects alive */
  liveSnapshots = new Set<WorldSnapshot>();
  /** hooks for game-level systems (set by the game layer) */
  beforeFixedUpdate: (() => void) | null = null;
  afterStep: (() => void) | null = null;

  constructor(gravity: V2 = { x: 0, y: -33 }) {
    const S = planck.Settings;
    S.linearSlop = 0.005; // polygon radius 0.01 = Physics2D.defaultContactOffset
    S.velocityThreshold = 1; // Physics2D.bounceThreshold
    S.maxLinearCorrection = 0.2;
    S.maxAngularCorrection = (8 * Math.PI) / 180;
    S.maxTranslation = 100 * FIXED_DT; // maxTranslationSpeed 100
    S.maxRotation = ((360 * Math.PI) / 180) * FIXED_DT;
    S.baumgarte = 0.2;
    S.toiBaugarte = 0.75;
    S.timeToSleep = 0.5;
    S.linearSleepTolerance = 0.01;
    S.angularSleepTolerance = (2 * Math.PI) / 180;
    this.pw = new planck.World({ gravity: new planck.Vec2(gravity.x, gravity.y), blockSolve: true, warmStarting: true, continuousPhysics: true, subStepping: false });
    this.ground = this.pw.createBody({ type: 'static' });
    this.pw.on('pre-solve', (c, old) => this.preSolve(c, old));
  }

  addGo(g: SimGo) {
    this.gos.push(g);
  }

  addBehaviour<T extends Behaviour>(b: T): T {
    b.go.behaviours.push(b);
    this.behaviours.push(b);
    this.behavioursDirty = true;
    this.pendingStart.push(b);
    b.awake?.();
    return b;
  }

  transformMoved(g: SimGo) {
    // colliders without their own rigidbody move with the transform: rebuild their fixtures
    const walk = (x: SimGo) => {
      if (x !== g && x.body) return;
      for (const c of x.colliders) if (c.fixtures.length) c.rebuild();
      x.children.forEach(walk);
    };
    if (!g.body) walk(g);
  }

  onGoDestroyed(g: SimGo) {
    let referenced = false;
    for (const s of this.liveSnapshots) if (s.gos.has(g)) referenced = true;
    if (referenced) return;
    const all = new Set(g.descendants());
    for (const x of all) if (x.body) this.pw.destroyBody(x.body.pb);
    this.gos = this.gos.filter((x) => !all.has(x));
    this.behaviours = this.behaviours.filter((b) => !all.has(b.go));
    this.pendingStart = this.pendingStart.filter((b) => !all.has(b.go));
    if (g.parent) g.parent.children = g.parent.children.filter((c) => c !== g);
  }

  private sortBehaviours() {
    if (!this.behavioursDirty) return;
    const idx = new Map(this.behaviours.map((b, i) => [b, i]));
    this.behaviours.sort((a, b) => a.order - b.order || idx.get(a)! - idx.get(b)!);
    this.behavioursDirty = false;
  }

  /** Run Start() on behaviours that have not started (and are on active objects). */
  runStarts() {
    let guard = 0;
    while (this.pendingStart.length && guard++ < 10) {
      const list = this.pendingStart;
      this.pendingStart = [];
      const later: Behaviour[] = [];
      for (const b of list) {
        if (b.go.destroyed && !this.behaviours.includes(b)) continue;
        if (!b.go.active || b.go.destroyed) {
          later.push(b);
          continue;
        }
        b.started = true;
        b.start?.();
      }
      this.pendingStart.push(...later);
      if (later.length === list.length) break;
    }
  }

  step() {
    this.runStarts();
    this.sortBehaviours();
    this.beforeFixedUpdate?.();
    for (const b of this.behaviours.slice()) {
      if (!b.started || !b.enabled || !b.go.active) continue;
      b.fixedUpdate?.();
    }
    for (const g of this.gos) g.body?.preStep();
    this.pw.step(FIXED_DT, 8, 3);
    // parents precede children in `gos`, so a parent's pose is final before its children follow it
    for (const g of this.gos) {
      if (!g.body) continue;
      if (g.body.followsParent()) {
        const m = mul(g.parent!.worldXform(), g.local);
        g.body.pb.setTransform(new planck.Vec2(m.tx, m.ty), Math.atan2(m.b, m.a));
      }
      g.body.postStep();
      g.markDirty();
    }
    this.frame++;
    this.time += FIXED_DT;
    this.dispatchContacts();
    this.afterStep?.();
  }

  /** Update() of all behaviours (cosmetic, once per rendered frame) */
  renderUpdate(dt: number) {
    for (const b of this.behaviours) {
      if (!b.started || !b.enabled || !b.go.active || !b.update) continue;
      b.update(dt);
    }
    for (const b of this.behaviours) {
      if (!b.started || !b.enabled || !b.go.active || !b.lateUpdate) continue;
      b.lateUpdate(dt);
    }
  }

  // ---------- contacts ----------

  private preSolve(c: planck.Contact, old: planck.Manifold) {
    const fa = c.getFixtureA(), fb = c.getFixtureB();
    const ca = fa.getUserData() as SimCollider | null, cb = fb.getUserData() as SimCollider | null;
    if (!ca || !cb) return;
    if (ignoredPair(ca, cb)) {
      c.setEnabled(false);
      return;
    }
    const ma = ca.effectiveMaterial(), mb = cb.effectiveMaterial();
    c.setFriction(combine(ma.friction, mb.friction, ma.frictionCombine, mb.frictionCombine));
    c.setRestitution(combine(ma.bounciness, mb.bounciness, ma.bounceCombine, mb.bounceCombine));
    const eff = ca.usedByEffector && ca.effector?.useOneWay ? ca : cb.usedByEffector && cb.effector?.useOneWay ? cb : null;
    if (eff) {
      let ok = this.oneWay.get(c);
      if (ok === undefined || old.pointCount === 0) {
        const wm = c.getWorldManifold(null);
        if (!wm) return;
        // normal pointing from the effector towards the other body
        const n = eff === ca ? wm.normal : { x: -wm.normal.x, y: -wm.normal.y };
        const up = eff.go.up;
        const ang = (Math.acos(Math.max(-1, Math.min(1, n.x * up.x + n.y * up.y))) * 180) / Math.PI;
        ok = ang <= eff.effector!.arc / 2 + 1e-3;
        this.oneWay.set(c, ok);
      }
      if (!ok) c.setEnabled(false);
    }
  }

  private pairKey(a: planck.Fixture, b: planck.Fixture): string {
    const ia = (a as any).__id ?? ((a as any).__id = ++fixtureSerial);
    const ib = (b as any).__id ?? ((b as any).__id = ++fixtureSerial);
    return ia < ib ? `${ia}:${ib}` : `${ib}:${ia}`;
  }

  /** collider-level touching pairs after the step, with Unity enter/stay/exit dispatch */
  private dispatchContacts() {
    const now = new Map<string, PairState>();
    for (let c = this.pw.getContactList(); c; c = c.getNext()) {
      if (!c.isTouching()) continue;
      const fa = c.getFixtureA(), fb = c.getFixtureB();
      const ca = fa.getUserData() as SimCollider | null, cb = fb.getUserData() as SimCollider | null;
      if (!ca || !cb || ca === cb || ignoredPair(ca, cb)) continue;
      const sensor = fa.isSensor() || fb.isSensor();
      const k2 = colliderPairKey(ca, cb);
      if (!now.has(k2)) now.set(k2, { a: ca, b: cb, sensor, seen: this.frame });
    }
    this.kinematicSensorPairs(now);
    const prev = this.pairs;
    this.pairs = now;
    const exits: PairState[] = [];
    for (const [k, p] of prev) if (!now.has(k)) exits.push(p);
    for (const [k, p] of now) {
      const was = prev.get(k);
      if (was && was.sensor !== p.sensor) {
        // trigger <-> solid switch: exit the old kind, enter the new one
        exits.push(was);
        this.emitPair(p, 'enter');
      } else this.emitPair(p, was ? 'stay' : 'enter');
    }
    for (const p of exits) this.emitPair(p, 'exit');
  }

  /**
   * Box2D never pairs two non-dynamic bodies, but Unity reports triggers between a kinematic trigger and
   * static / kinematic colliders (projectiles hitting walls): find those overlaps with a broadphase query.
   */
  private kinematicSensorPairs(now: Map<string, PairState>) {
    const aabb = new planck.AABB();
    const childBox = new planck.AABB();
    for (let body = this.pw.getBodyList(); body; body = body.getNext()) {
      if (!body.isKinematic() || !body.isActive()) continue;
      for (let f = body.getFixtureList(); f; f = f.getNext()) {
        if (!f.isSensor()) continue;
        const ca = f.getUserData() as SimCollider | null;
        if (!ca) continue;
        const shape = f.getShape();
        const xf = body.getTransform();
        for (let i = 0; i < shape.getChildCount(); i++) {
          shape.computeAABB(aabb, xf, i);
          const visited = new Set<planck.Fixture>();
          this.pw.queryAABB(aabb, (other) => {
            if (visited.has(other)) return true;
            visited.add(other);
            const ob = other.getBody();
            if (ob === body || ob.isDynamic()) return true;
            const cb = other.getUserData() as SimCollider | null;
            if (!cb || cb === ca) return true;
            if ((f.getFilterCategoryBits() & other.getFilterMaskBits()) === 0 || (other.getFilterCategoryBits() & f.getFilterMaskBits()) === 0) return true;
            const key = colliderPairKey(ca, cb);
            if (now.has(key)) return true;
            const s2 = other.getShape();
            const x2 = ob.getTransform();
            for (let j = 0; j < s2.getChildCount(); j++) {
              if (s2.getChildCount() > 1) {
                s2.computeAABB(childBox, x2, j);
                if (!planck.AABB.testOverlap(aabb, childBox)) continue;
              }
              if (planck.testOverlap(shape, i, s2, j, xf, x2)) {
                now.set(key, { a: ca, b: cb, sensor: true, seen: this.frame });
                break;
              }
            }
            return true;
          });
        }
      }
    }
  }

  private emitPair(p: PairState, phase: 'enter' | 'stay' | 'exit') {
    const send = (self: SimCollider, other: SimCollider) => {
      const targets: SimGo[] = [self.go];
      const rb = self.attachedRigidbody;
      if (rb && rb.go !== self.go) targets.push(rb.go);
      let col: Collision2D | null = null;
      for (const g of targets) {
        if (!g.active && phase !== 'exit') continue;
        for (const b of g.behaviours.slice()) {
          if (!b.started) continue;
          if (p.sensor) {
            if (phase === 'enter') b.onTriggerEnter?.(other, self);
            else if (phase === 'stay') b.onTriggerStay?.(other, self);
            else b.onTriggerExit?.(other, self);
          } else {
            const fn = phase === 'enter' ? b.onCollisionEnter : phase === 'stay' ? b.onCollisionStay : b.onCollisionExit;
            if (!fn) continue;
            col ??= this.makeCollision(self, other, phase !== 'exit');
            fn.call(b, col);
          }
        }
      }
    };
    send(p.a, p.b);
    send(p.b, p.a);
  }

  private makeCollision(self: SimCollider, other: SimCollider, withContacts: boolean): Collision2D {
    const contacts: ContactPoint[] = [];
    if (withContacts) {
      for (const f of self.fixtures) {
        for (let e = f.getBody().getContactList(); e; e = e.next) {
          const c = e.contact;
          const fa = c.getFixtureA(), fb = c.getFixtureB();
          if (!((fa === f && fb.getUserData() === other) || (fb === f && fa.getUserData() === other))) continue;
          this.pushContactPoints(c, f, contacts);
        }
      }
    }
    const rbA = self.attachedRigidbody, rbB = other.attachedRigidbody;
    const va = rbA ? rbA.velocity : { x: 0, y: 0 }, vb = rbB ? rbB.velocity : { x: 0, y: 0 };
    return {
      collider: other,
      otherCollider: self,
      rigidbody: rbB,
      gameObject: rbB ? rbB.go : other.go,
      contacts,
      relativeVelocity: { x: vb.x - va.x, y: vb.y - va.y },
    };
  }

  private pushContactPoints(c: planck.Contact, selfFixture: planck.Fixture, out: ContactPoint[]) {
    if (!c.isTouching()) return;
    const fa = c.getFixtureA(), fb = c.getFixtureB();
    if (fa.isSensor() || fb.isSensor()) return;
    const man = c.getManifold();
    if (!man.pointCount) return;
    const wm = c.getWorldManifold(null);
    if (!wm) return;
    const selfIsA = fa === selfFixture;
    const otherF = selfIsA ? fb : fa;
    const n = selfIsA ? { x: -wm.normal.x, y: -wm.normal.y } : { x: wm.normal.x, y: wm.normal.y };
    const other = otherF.getUserData() as SimCollider;
    const self = selfFixture.getUserData() as SimCollider;
    if (other && self && ignoredPair(self, other)) return;
    for (let i = 0; i < man.pointCount; i++) {
      out.push({
        normal: n,
        point: { x: wm.points[i].x, y: wm.points[i].y },
        normalImpulse: man.points[i].normalImpulse,
        separation: wm.separations[i],
        collider: other,
        otherCollider: self,
        rigidbody: other.attachedRigidbody,
        enabled: c.isEnabled(),
      });
    }
  }

  /** Rigidbody2D.GetContacts */
  contactsOf(b: SimBody): ContactPoint[] {
    const out: ContactPoint[] = [];
    for (let e = b.pb.getContactList(); e; e = e.next) {
      const c = e.contact;
      const fa = c.getFixtureA(), fb = c.getFixtureB();
      const self = fa.getBody() === b.pb ? fa : fb;
      if (fa.getBody() === b.pb && fb.getBody() === b.pb) continue;
      this.pushContactPoints(c, self, out);
    }
    return out;
  }

  /** Collider2D.GetContacts (single collider) */
  colliderContacts(col: SimCollider): ContactPoint[] {
    const out: ContactPoint[] = [];
    for (const f of col.fixtures) {
      for (let e = f.getBody().getContactList(); e; e = e.next) {
        const c = e.contact;
        if (c.getFixtureA() !== f && c.getFixtureB() !== f) continue;
        this.pushContactPoints(c, f, out);
      }
    }
    return out;
  }

  // ---------- queries ----------

  private queryShape(shape: planck.Shape, xf: planck.Transform, aabb: planck.AABB, filter?: (c: SimCollider) => boolean): SimCollider[] {
    const out = new Set<SimCollider>();
    this.pw.queryAABB(aabb, (fixture) => {
      const c = fixture.getUserData() as SimCollider | null;
      if (!c || out.has(c)) return true;
      if (filter && !filter(c)) return true;
      const s2 = fixture.getShape();
      const x2 = fixture.getBody().getTransform();
      for (let j = 0; j < s2.getChildCount(); j++) {
        if (planck.testOverlap(shape, 0, s2, j, xf, x2)) {
          out.add(c);
          break;
        }
      }
      return true;
    });
    return [...out];
  }

  /** Physics2D.OverlapBoxAll (queries hit triggers) */
  overlapBox(center: V2, size: V2, angleDeg = 0, filter?: (c: SimCollider) => boolean): SimCollider[] {
    const hw = Math.abs(size.x) / 2, hh = Math.abs(size.y) / 2;
    if (hw <= 0 || hh <= 0) return [];
    const shape = new planck.Box(hw, hh);
    shape.m_radius = 0;
    const xf = new planck.Transform(new planck.Vec2(center.x, center.y), (angleDeg * Math.PI) / 180);
    const r = Math.hypot(hw, hh);
    const aabb = new planck.AABB(new planck.Vec2(center.x - r, center.y - r), new planck.Vec2(center.x + r, center.y + r));
    return this.queryShape(shape, xf, aabb, filter);
  }

  overlapCircle(center: V2, radius: number, filter?: (c: SimCollider) => boolean): SimCollider[] {
    const shape = new planck.Circle(new planck.Vec2(0, 0), radius);
    const xf = new planck.Transform(new planck.Vec2(center.x, center.y), 0);
    const aabb = new planck.AABB(new planck.Vec2(center.x - radius, center.y - radius), new planck.Vec2(center.x + radius, center.y + radius));
    return this.queryShape(shape, xf, aabb, filter);
  }

  /** Collider2D.Overlap: colliders overlapping `col` (triggers included, `col` itself excluded) */
  overlapCollider(col: SimCollider, filter?: (c: SimCollider) => boolean): SimCollider[] {
    const out = new Set<SimCollider>();
    for (const f of col.fixtures) {
      const shape = f.getShape();
      const xf = f.getBody().getTransform();
      for (let i = 0; i < shape.getChildCount(); i++) {
        const aabb = new planck.AABB();
        shape.computeAABB(aabb, xf, i);
        this.pw.queryAABB(aabb, (other) => {
          const c = other.getUserData() as SimCollider | null;
          if (!c || c === col || out.has(c)) return true;
          if (filter && !filter(c)) return true;
          const s2 = other.getShape();
          const x2 = other.getBody().getTransform();
          for (let j = 0; j < s2.getChildCount(); j++) {
            if (planck.testOverlap(shape, i, s2, j, xf, x2)) {
              out.add(c);
              break;
            }
          }
          return true;
        });
      }
    }
    return [...out];
  }

  overlapPoint(p: V2, filter?: (c: SimCollider) => boolean): SimCollider[] {
    return this.overlapCircle(p, 0.001, filter);
  }

  /** Physics2D.Raycast: first hit (closest) */
  raycast(from: V2, dir: V2, dist: number, filter?: (c: SimCollider) => boolean): { collider: SimCollider; point: V2; normal: V2; distance: number } | null {
    const l = Math.hypot(dir.x, dir.y) || 1;
    const to = new planck.Vec2(from.x + (dir.x / l) * dist, from.y + (dir.y / l) * dist);
    let best: { collider: SimCollider; point: V2; normal: V2; distance: number } | null = null;
    this.pw.rayCast(new planck.Vec2(from.x, from.y), to, (fixture, point, normal, fraction) => {
      const c = fixture.getUserData() as SimCollider | null;
      if (!c || (filter && !filter(c))) return -1;
      best = { collider: c, point: { x: point.x, y: point.y }, normal: { x: normal.x, y: normal.y }, distance: fraction * dist };
      return fraction;
    });
    return best;
  }

  /** Physics2D.Raycast into a buffer: every collider hit (closest point each), sorted by distance;
   * colliders containing the origin count at distance 0 (queriesStartInColliders) */
  raycastAll(from: V2, dir: V2, dist: number, filter?: (c: SimCollider) => boolean): { collider: SimCollider; point: V2; normal: V2; distance: number }[] {
    const l = Math.hypot(dir.x, dir.y) || 1;
    const p1 = new planck.Vec2(from.x, from.y);
    const to = new planck.Vec2(from.x + (dir.x / l) * dist, from.y + (dir.y / l) * dist);
    const best = new Map<SimCollider, { collider: SimCollider; point: V2; normal: V2; distance: number }>();
    const aabb = new planck.AABB(new planck.Vec2(from.x - 1e-4, from.y - 1e-4), new planck.Vec2(from.x + 1e-4, from.y + 1e-4));
    this.pw.queryAABB(aabb, (fixture) => {
      const c = fixture.getUserData() as SimCollider | null;
      if (!c || best.has(c) || (filter && !filter(c))) return true;
      if (fixture.testPoint(p1)) best.set(c, { collider: c, point: { x: from.x, y: from.y }, normal: { x: -dir.x / l, y: -dir.y / l }, distance: 0 });
      return true;
    });
    this.pw.rayCast(p1, to, (fixture, point, normal, fraction) => {
      const c = fixture.getUserData() as SimCollider | null;
      if (!c || (filter && !filter(c))) return -1;
      const d = fraction * dist;
      const prev = best.get(c);
      if (!prev || d < prev.distance) best.set(c, { collider: c, point: { x: point.x, y: point.y }, normal: { x: normal.x, y: normal.y }, distance: d });
      return 1;
    });
    return [...best.values()].sort((a, b) => a.distance - b.distance);
  }

  // ---------- snapshots ----------

  snapshot(extra: any = null, full = false): WorldSnapshot {
    const gos = new Map<SimGo, GoState>();
    for (const g of this.gos) {
      if (g.destroyed || (g.noSnapshot && !full)) continue;
      const st: GoState = {
        activeSelf: g.activeSelf,
        destroyed: false,
        local: g.local,
        scale: [...g.bodyScaleXY] as [number, number],
        colliders: g.colliders.map((c) => ({ enabled: c.enabled, isTrigger: c.isTrigger, shape: c.shape, material: c.material })),
        behaviours: g.behaviours.map((b) => saveBehaviour(b, full)),
      };
      if (g.body) {
        const b = g.body;
        st.body = { type: b.type, pos: b.position, angle: b.pb.getAngle(), vel: b.velocity, angVel: b.pb.getAngularVelocity(), simulated: b.simulated, gravityScale: b.gravityScale, mass: b.mass, kv: { ...b.kvVelocity } };
      }
      if (g.sprite) st.sprite = { ...g.sprite, color: [...g.sprite.color] as RGBA, size: [...g.sprite.size] as [number, number] };
      if (g.tilemap) st.tilemap = { tiles: g.tilemap.tiles, color: [...g.tilemap.color] as RGBA, enabled: g.tilemap.enabled, order: g.tilemap.order, sortingLayer: g.tilemap.sortingLayer };
      gos.set(g, st);
    }
    return { frame: this.frame, gos, extra, full };
  }

  /** Restore a snapshot. Objects created after it are destroyed (except `keep`), destroyed ones come back. */
  restore(s: WorldSnapshot, keep: Set<SimGo> = new Set()) {
    for (const g of this.gos.slice()) if (!s.gos.has(g) && !keep.has(g) && !g.destroyed && (s.full || !g.noSnapshot)) g.destroy();
    const present = new Set(this.gos);
    const entries = [...s.gos].filter(([g]) => present.has(g));
    const moved = new Set<SimGo>();
    const scaled = new Set<SimGo>();
    for (const [g, st] of entries) {
      if (g.local !== st.local) moved.add(g);
      if (g.bodyScaleXY[0] !== st.scale[0] || g.bodyScaleXY[1] !== st.scale[1]) scaled.add(g);
      g.destroyed = false;
      (g as any)._activeSelf = st.activeSelf;
      g.local = st.local;
      (g as any).bodyScale = [...st.scale];
      g.markDirty();
    }
    for (const [g, st] of entries) {
      if (g.body && st.body) {
        const b = g.body;
        b.type = st.body.type;
        b.simulated = st.body.simulated;
        b.pb.setTransform(new planck.Vec2(st.body.pos.x, st.body.pos.y), st.body.angle);
        b.pb.setLinearVelocity(new planck.Vec2(st.body.vel.x, st.body.vel.y));
        b.pb.setAngularVelocity(st.body.angVel);
        b.gravityScale = st.body.gravityScale;
        b.mass = st.body.mass;
        b.moveTarget = null;
        b.moveRotTarget = null;
        b.resetKV();
        b.kvVelocity = { ...st.body.kv };
        b.pb.setAwake(true);
      }
      g.markDirty();
    }
    for (const [g, st] of entries) {
      g.body?.syncActive();
      g.colliders.forEach((c, i) => {
        const cs = st.colliders[i];
        if (!cs) return;
        const shapeChanged = cs.shape !== c.shape;
        c.material = cs.material;
        c.shape = cs.shape;
        c.isTrigger = cs.isTrigger;
        (c as any)._enabled = cs.enabled;
        let rebuild = shapeChanged || c.fixtures.some((f) => f.isSensor() !== cs.isTrigger);
        if (!rebuild && c.fixtures.length) {
          // fixtures live in the rigidbody frame: rebuild when a transform between collider and body changed
          const rb = c.attachedRigidbody;
          for (let x: SimGo | null = c.go; x && !rebuild; x = x.parent) {
            if (rb && x === rb.go) {
              rebuild = scaled.has(x);
              break;
            }
            if (moved.has(x)) rebuild = true;
          }
        }
        if (rebuild) c.destroyFixtures();
        c.sync();
      });
      g.body?.updateMass();
      if (g.sprite && st.sprite) Object.assign(g.sprite, { ...st.sprite, color: [...st.sprite.color], size: [...st.sprite.size] });
      if (g.tilemap && st.tilemap) Object.assign(g.tilemap, { ...st.tilemap, color: [...st.tilemap.color] });
      g.behaviours.forEach((b, i) => {
        const bs = st.behaviours[i];
        if (bs) loadBehaviour(b, bs);
      });
    }
    for (const [g] of entries) for (const b of g.behaviours) b.afterRestore?.();
  }
}

let fixtureSerial = 0;

function colliderPairKey(a: SimCollider, b: SimCollider): string {
  const ia = a.go.colliders.indexOf(a), ib = b.go.colliders.indexOf(b);
  return a.go.uid < b.go.uid || (a.go.uid === b.go.uid && ia < ib) ? `${a.go.uid}.${ia}|${b.go.uid}.${ib}` : `${b.go.uid}.${ib}|${a.go.uid}.${ia}`;
}
